import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Crown, Sparkles, CreditCard, Calendar, FileText, 
  ArrowUpRight, ArrowDownRight, RefreshCw, AlertCircle,
  Check, X, Loader2, Download, ExternalLink, ChevronRight
} from 'lucide-react';

const PLAN_INFO = {
  essentiel: {
    name: '🌿 Essentiel',
    description: 'Outils essentiels pour développer votre marque',
    color: 'emerald'
  },
  experience: {
    name: '👑 Vision Lab Experience',
    description: 'Expérience complète avec coaching IA premium',
    color: 'amber'
  }
};

const MySubscription = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedChange, setSelectedChange] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch subscription details
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [detailsRes, invoicesRes] = await Promise.all([
          axios.get(`${API}/subscriptions/details`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/subscriptions/invoices`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        
        setDetails(detailsRes.data);
        setInvoices(invoicesRes.data.invoices || []);
      } catch (error) {
        console.error('Error fetching subscription data:', error);
        toast.error('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchData();
  }, [token]);

  // Preview subscription change
  const previewChange = async (newPlan, newCycle) => {
    setPreviewLoading(true);
    try {
      const response = await axios.post(
        `${API}/subscriptions/preview-change`,
        { new_plan: newPlan, new_billing_cycle: newCycle },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreviewData(response.data.preview);
      setSelectedChange({ plan: newPlan, cycle: newCycle });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la prévisualisation');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Execute upgrade
  const executeUpgrade = async () => {
    if (!selectedChange) return;
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/subscriptions/upgrade`,
        { new_plan: selectedChange.plan, new_billing_cycle: selectedChange.cycle },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Mise à niveau effectuée avec succès !');
      setShowUpgradeModal(false);
      // Refresh data
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à niveau');
    } finally {
      setActionLoading(false);
    }
  };

  // Execute downgrade
  const executeDowngrade = async () => {
    if (!selectedChange) return;
    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API}/subscriptions/downgrade`,
        { new_plan: selectedChange.plan, new_billing_cycle: selectedChange.cycle },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setShowDowngradeModal(false);
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement');
    } finally {
      setActionLoading(false);
    }
  };

  // Cancel subscription
  const cancelSubscription = async () => {
    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API}/subscriptions/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Annulation programmée');
      setShowCancelModal(false);
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
    } finally {
      setActionLoading(false);
    }
  };

  // Reactivate subscription
  const reactivateSubscription = async () => {
    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API}/subscriptions/reactivate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success('Abonnement réactivé !');
        window.location.reload();
      } else {
        navigate('/pricing');
      }
    } catch (error) {
      toast.error('Erreur lors de la réactivation');
    } finally {
      setActionLoading(false);
    }
  };

  // Open payment method portal
  const openPaymentPortal = async () => {
    try {
      const response = await axios.post(
        `${API}/subscriptions/payment-method-session`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Erreur lors de l\'ouverture du portail de paiement');
    }
  };

  // Retry failed payment
  const retryPayment = async () => {
    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API}/subscriptions/retry-payment`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success('Paiement effectué !');
        window.location.reload();
      } else {
        toast.error(response.data.message);
        if (response.data.action === 'update_payment_method') {
          openPaymentPortal();
        }
      }
    } catch (error) {
      toast.error('Erreur lors du paiement');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Mon abonnement">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!details) {
    return (
      <DashboardLayout title="Mon abonnement">
        <div className="text-center py-12">
          <p className="text-[#A1A1AA]">Impossible de charger les données</p>
          <Button onClick={() => window.location.reload()} className="mt-4 btn-gold">
            Réessayer
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const { subscription, founding_visionary, payment_method, available_plans } = details;
  const currentPlanInfo = PLAN_INFO[subscription.tier] || { name: subscription.tier, description: '' };
  const isExperience = subscription.tier === 'experience';
  const isAnnual = subscription.billing_cycle === 'annual';
  const isCancelling = subscription.cancel_at_period_end;

  return (
    <DashboardLayout title="Mon abonnement">
      <div className="max-w-4xl mx-auto space-y-8">
        
        {/* Founding Visionary Banner */}
        {founding_visionary.is_founding_visionary && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-[#D4AF37]/20 to-[#E5C585]/10 border border-[#D4AF37]/30 rounded-xl p-6"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#D4AF37]/20 rounded-full flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <div>
                <h3 className="text-[#D4AF37] font-serif text-lg">{founding_visionary.badge}</h3>
                <p className="text-[#A1A1AA] text-sm">
                  Réduction permanente de {founding_visionary.discount_percent}% sur tous vos abonnements
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Payment Alert */}
        {subscription.status === 'past_due' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-red-900/20 border border-red-500/30 rounded-xl p-6"
          >
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="text-red-400 font-medium mb-1">Paiement en attente</h3>
                <p className="text-[#A1A1AA] text-sm mb-4">
                  Votre dernier paiement a échoué. Veuillez mettre à jour votre méthode de paiement pour conserver votre accès.
                </p>
                <div className="flex gap-3">
                  <Button onClick={retryPayment} disabled={actionLoading} className="bg-red-600 hover:bg-red-700 text-white">
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Réessayer le paiement'}
                  </Button>
                  <Button onClick={openPaymentPortal} variant="outline" className="border-red-500/30 text-red-400">
                    Modifier la carte
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Cancellation Notice */}
        {isCancelling && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-amber-900/20 border border-amber-500/30 rounded-xl p-6"
          >
            <div className="flex items-start gap-4">
              <Calendar className="w-6 h-6 text-amber-400 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="text-amber-400 font-medium mb-1">Annulation programmée</h3>
                <p className="text-[#A1A1AA] text-sm mb-4">
                  Votre abonnement prendra fin le{' '}
                  <span className="text-white">
                    {new Date(subscription.renewal_date).toLocaleDateString('fr-FR', {
                      day: 'numeric', month: 'long', year: 'numeric'
                    })}
                  </span>
                  . Vous conservez l&apos;accès jusque-là.
                </p>
                <Button onClick={reactivateSubscription} disabled={actionLoading} className="btn-gold">
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Réactiver mon abonnement'}
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Current Plan Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-b from-[#0A0A0A] to-[#111] border border-[#D4AF37]/20 rounded-2xl overflow-hidden"
        >
          <div className="p-8">
            <div className="flex items-start justify-between mb-6">
              <div>
                <span className="text-[#52525B] text-xs font-accent tracking-wider">PLAN ACTUEL</span>
                <h2 className="text-white font-serif text-2xl mt-1">{currentPlanInfo.name}</h2>
                <p className="text-[#A1A1AA] text-sm mt-1">{currentPlanInfo.description}</p>
              </div>
              <div className="text-right">
                {subscription.current_price ? (
                  <>
                    <div className="text-[#D4AF37] font-serif text-3xl">
                      CHF {subscription.current_price}
                    </div>
                    <span className="text-[#A1A1AA] text-sm">
                      / {subscription.billing_cycle === 'annual' ? 'an' : 'mois'}
                    </span>
                  </>
                ) : subscription.status === 'beta' ? (
                  <div className="text-[#E5C585] font-serif text-xl">
                    Accès Beta
                  </div>
                ) : (
                  <div className="text-[#A1A1AA] font-serif text-xl">
                    -
                  </div>
                )}
                {founding_visionary.is_founding_visionary && subscription.current_price && (
                  <div className="text-[#E5C585] text-xs mt-1">
                    -{founding_visionary.discount_percent}% appliqué
                  </div>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="bg-[#0A0A0A] p-4 rounded-lg border border-[#27272A]">
                <span className="text-[#52525B] text-xs">STATUT</span>
                <div className="flex items-center gap-2 mt-1">
                  <div className={`w-2 h-2 rounded-full ${
                    subscription.status === 'active' ? 'bg-green-400' : 
                    subscription.status === 'past_due' ? 'bg-red-400' : 'bg-amber-400'
                  }`} />
                  <span className="text-white capitalize">
                    {subscription.status === 'active' ? 'Actif' : 
                     subscription.status === 'past_due' ? 'Paiement en attente' : 
                     subscription.status}
                  </span>
                </div>
              </div>
              <div className="bg-[#0A0A0A] p-4 rounded-lg border border-[#27272A]">
                <span className="text-[#52525B] text-xs">FACTURATION</span>
                <p className="text-white mt-1">
                  {subscription.billing_cycle === 'annual' ? 'Annuelle' : 'Mensuelle'}
                </p>
              </div>
              <div className="bg-[#0A0A0A] p-4 rounded-lg border border-[#27272A]">
                <span className="text-[#52525B] text-xs">PROCHAIN RENOUVELLEMENT</span>
                <p className="text-white mt-1">
                  {subscription.renewal_date 
                    ? new Date(subscription.renewal_date).toLocaleDateString('fr-FR', {
                        day: 'numeric', month: 'long', year: 'numeric'
                      })
                    : '-'
                  }
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            {!isCancelling && subscription.status === 'active' && (
              <div className="flex flex-wrap gap-3">
                {!isExperience && (
                  <Button 
                    onClick={() => { setShowUpgradeModal(true); previewChange('experience', subscription.billing_cycle); }}
                    className="btn-gold flex items-center gap-2"
                    data-testid="upgrade-plan-btn"
                  >
                    <ArrowUpRight size={18} />
                    Passer à Experience
                  </Button>
                )}
                {!isAnnual && (
                  <Button 
                    onClick={() => { setShowUpgradeModal(true); previewChange(subscription.tier, 'annual'); }}
                    className="btn-outline-gold flex items-center gap-2"
                    data-testid="upgrade-annual-btn"
                  >
                    <ArrowUpRight size={18} />
                    Passer à l&apos;annuel (économisez 2 mois)
                  </Button>
                )}
                {isExperience && (
                  <Button 
                    onClick={() => { setShowDowngradeModal(true); previewChange('essentiel', subscription.billing_cycle); }}
                    className="bg-transparent border border-[#27272A] text-[#A1A1AA] hover:border-[#D4AF37]/30 flex items-center gap-2"
                    data-testid="downgrade-plan-btn"
                  >
                    <ArrowDownRight size={18} />
                    Passer à Essentiel
                  </Button>
                )}
              </div>
            )}

            {/* CTA for Beta users to subscribe */}
            {subscription.status === 'beta' && (
              <div className="mt-6 pt-6 border-t border-[#27272A]">
                <p className="text-[#A1A1AA] text-sm mb-4">
                  Votre accès Beta vous permet de découvrir toutes les fonctionnalités de Vision Lab. 
                  Pour conserver votre accès après la période Beta, choisissez votre formule d&apos;abonnement.
                </p>
                <Button 
                  onClick={() => navigate('/pricing')}
                  className="btn-gold flex items-center gap-2"
                  data-testid="beta-subscribe-btn"
                >
                  <Crown size={18} />
                  Choisir mon abonnement
                  <ChevronRight size={16} />
                </Button>
                {founding_visionary.is_founding_visionary && (
                  <p className="text-[#E5C585] text-xs mt-3">
                    En tant que Founding Visionary, vous bénéficierez de {founding_visionary.discount_percent}% de réduction à vie sur votre abonnement.
                  </p>
                )}
              </div>
            )}
          </div>
        </motion.div>

        {/* Payment Method */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-b from-[#0A0A0A] to-[#111] border border-[#27272A] rounded-2xl p-8"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <CreditCard className="text-[#D4AF37]" size={24} />
              <h3 className="font-serif text-xl text-white">Moyen de paiement</h3>
            </div>
            <Button 
              onClick={openPaymentPortal}
              className="btn-outline-gold text-sm"
              data-testid="update-payment-btn"
            >
              Modifier
            </Button>
          </div>

          {payment_method ? (
            <div className="flex items-center gap-4 bg-[#0A0A0A] p-4 rounded-lg border border-[#27272A]">
              <div className="w-12 h-8 bg-[#1C1C1C] rounded flex items-center justify-center">
                <span className="text-white text-xs font-bold uppercase">{payment_method.brand}</span>
              </div>
              <div>
                <p className="text-white">•••• •••• •••• {payment_method.last4}</p>
                <p className="text-[#A1A1AA] text-sm">
                  Expire {payment_method.exp_month}/{payment_method.exp_year}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-[#A1A1AA]">Aucun moyen de paiement enregistré</p>
          )}
        </motion.div>

        {/* Invoices */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-b from-[#0A0A0A] to-[#111] border border-[#27272A] rounded-2xl p-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <FileText className="text-[#E5C585]" size={24} />
            <h3 className="font-serif text-xl text-white">Factures</h3>
          </div>

          {invoices.length > 0 ? (
            <div className="space-y-3">
              {invoices.slice(0, 6).map((invoice) => (
                <div 
                  key={invoice.id}
                  className="flex items-center justify-between bg-[#0A0A0A] p-4 rounded-lg border border-[#27272A] hover:border-[#D4AF37]/20 transition-colors"
                >
                  <div>
                    <p className="text-white">{invoice.created_display}</p>
                    <p className="text-[#A1A1AA] text-sm">
                      {invoice.currency} {invoice.amount.toFixed(2)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {invoice.pdf_url && (
                      <a
                        href={invoice.pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
                        data-testid={`download-invoice-${invoice.id}`}
                      >
                        <Download size={18} />
                      </a>
                    )}
                    {invoice.hosted_url && (
                      <a
                        href={invoice.hosted_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
                      >
                        <ExternalLink size={18} />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#A1A1AA]">Aucune facture disponible</p>
          )}
        </motion.div>

        {/* Cancel Subscription */}
        {!isCancelling && subscription.status === 'active' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-center py-8"
          >
            <Button 
              onClick={() => setShowCancelModal(true)}
              className="text-[#71717A] hover:text-red-400 bg-transparent hover:bg-transparent"
              data-testid="cancel-subscription-btn"
            >
              Annuler mon abonnement
            </Button>
          </motion.div>
        )}

        {/* Upgrade Modal */}
        <AnimatePresence>
          {showUpgradeModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setShowUpgradeModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-[#0A0A0A] border border-[#D4AF37]/30 rounded-2xl p-8 max-w-lg w-full"
                onClick={e => e.stopPropagation()}
              >
                <h2 className="font-serif text-2xl text-white mb-2">Confirmer la mise à niveau</h2>
                
                {previewLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
                  </div>
                ) : previewData ? (
                  <>
                    <p className="text-[#A1A1AA] mb-6">
                      {previewData.is_upgrade 
                        ? `Vous allez passer à ${previewData.new_plan.name}. Vos fonctionnalités premium seront disponibles immédiatement après confirmation du paiement.`
                        : ''}
                    </p>

                    <div className="space-y-4 mb-6">
                      <div className="flex justify-between items-center">
                        <span className="text-[#A1A1AA]">Plan actuel</span>
                        <span className="text-white">{previewData.current_plan.name}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[#A1A1AA]">Nouveau plan</span>
                        <span className="text-[#D4AF37]">{previewData.new_plan.name}</span>
                      </div>
                      <div className="border-t border-[#27272A] pt-4">
                        <div className="flex justify-between items-center">
                          <span className="text-[#A1A1AA]">Prise d&apos;effet</span>
                          <span className="text-white">{previewData.effective_date_display}</span>
                        </div>
                      </div>
                      {previewData.proration_display && (
                        <div className="flex justify-between items-center">
                          <span className="text-[#A1A1AA]">Prorata à payer maintenant</span>
                          <span className="text-[#D4AF37]">{previewData.proration_display}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="text-[#A1A1AA]">Prochain renouvellement</span>
                        <span className="text-white">{previewData.next_renewal_display}</span>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <Button
                        onClick={() => setShowUpgradeModal(false)}
                        className="flex-1 bg-transparent border border-[#27272A] text-white hover:bg-[#1C1C1C]"
                      >
                        Annuler
                      </Button>
                      <Button
                        onClick={executeUpgrade}
                        disabled={actionLoading}
                        className="flex-1 btn-gold"
                        data-testid="confirm-upgrade-btn"
                      >
                        {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmer la mise à niveau'}
                      </Button>
                    </div>
                  </>
                ) : null}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Downgrade Modal */}
        <AnimatePresence>
          {showDowngradeModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setShowDowngradeModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-[#0A0A0A] border border-[#27272A] rounded-2xl p-8 max-w-lg w-full"
                onClick={e => e.stopPropagation()}
              >
                <h2 className="font-serif text-2xl text-white mb-2">Changer de formule</h2>
                
                {previewLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
                  </div>
                ) : previewData ? (
                  <>
                    <p className="text-[#A1A1AA] mb-6">
                      Votre abonnement actuel restera actif jusqu&apos;à la fin de votre période de facturation.
                      Votre nouveau plan commencera automatiquement le {previewData.effective_date_display}.
                    </p>

                    <div className="space-y-4 mb-6">
                      <div className="flex justify-between items-center">
                        <span className="text-[#A1A1AA]">Plan actuel</span>
                        <span className="text-white">{previewData.current_plan.name}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[#A1A1AA]">Nouveau plan</span>
                        <span className="text-white">{previewData.new_plan.name}</span>
                      </div>
                      <div className="border-t border-[#27272A] pt-4">
                        <div className="flex justify-between items-center">
                          <span className="text-[#A1A1AA]">Date d&apos;effet</span>
                          <span className="text-white">{previewData.effective_date_display}</span>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[#A1A1AA]">Nouveau tarif</span>
                        <span className="text-white">{previewData.next_renewal_display} / {previewData.new_plan.billing_cycle === 'annual' ? 'an' : 'mois'}</span>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <Button
                        onClick={() => setShowDowngradeModal(false)}
                        className="flex-1 bg-transparent border border-[#27272A] text-white hover:bg-[#1C1C1C]"
                      >
                        Annuler
                      </Button>
                      <Button
                        onClick={executeDowngrade}
                        disabled={actionLoading}
                        className="flex-1 btn-gold"
                        data-testid="confirm-downgrade-btn"
                      >
                        {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmer'}
                      </Button>
                    </div>
                  </>
                ) : null}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Cancel Modal */}
        <AnimatePresence>
          {showCancelModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setShowCancelModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-[#0A0A0A] border border-red-500/30 rounded-2xl p-8 max-w-lg w-full"
                onClick={e => e.stopPropagation()}
              >
                <h2 className="font-serif text-2xl text-white mb-4">Annuler votre abonnement</h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <p className="text-[#A1A1AA]">
                      Vous conservez l&apos;accès à toutes vos fonctionnalités jusqu&apos;à la fin de votre période de facturation
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <p className="text-[#A1A1AA]">
                      Vos projets et votre historique seront préservés pendant 90 jours
                    </p>
                  </div>
                  {founding_visionary.is_founding_visionary && (
                    <div className="flex items-start gap-3">
                      <Sparkles className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                      <p className="text-[#D4AF37]">
                        Votre statut Founding Visionary et votre réduction de 20% à vie resteront attachés à votre compte si vous vous réabonnez
                      </p>
                    </div>
                  )}
                </div>

                <div className="bg-[#1C1C1C] p-4 rounded-lg mb-6">
                  <p className="text-white text-sm">
                    Votre accès prendra fin le{' '}
                    <span className="text-[#D4AF37]">
                      {subscription.renewal_date 
                        ? new Date(subscription.renewal_date).toLocaleDateString('fr-FR', {
                            day: 'numeric', month: 'long', year: 'numeric'
                          })
                        : 'la fin de la période'
                      }
                    </span>
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowCancelModal(false)}
                    className="flex-1 btn-gold"
                  >
                    Garder mon abonnement
                  </Button>
                  <Button
                    onClick={cancelSubscription}
                    disabled={actionLoading}
                    className="flex-1 bg-transparent border border-red-500/30 text-red-400 hover:bg-red-500/10"
                    data-testid="confirm-cancel-btn"
                  >
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmer l\'annulation'}
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </DashboardLayout>
  );
};

export default MySubscription;
